import './ProductsComparison.less';
import useAppSelector from '../../../hooks/useAppSelector';
import { Product } from '../../../models/Product';
import useAppDispatch from '../../../hooks/useAppDispatch';
import ProductComparisonError from './ProductComparisonError';
import { useCallback, useRef, useState } from 'preact/hooks';
import ProductPrice from '../../product/ProductPrice';
import ProductsComparisonTable from './ProductsComparisonTable';
import Loader from '../../loader/Loader';
import { useTranslation } from '../../../hooks/useTranslation';
import { isProductComparisonFeatureItem } from '../../../services/FeatureService';

const ProductsComparison = () => {
    const { t } = useTranslation();
    const mainFeatureItem = useAppSelector(
        state => state.featureState.mainFeatureItem
    );
    const [isStickyHeaderVisible, setStickyHeaderVisible] = useState(false);
    const mainDataRef = useRef<HTMLAnchorElement>(null);
    const scrollableRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();

    const getStickyHeaderItem = useCallback(
        (product: Product | null) =>
            product ? (
                <div className="bri-ProductsComparison-sticky-header-item">
                    <img src={product.images[0].url} />
                    <span
                        className={`bri-BodyFont bri-ProductsComparison-sticky-header-name`}
                    >
                        {product.name}
                    </span>
                    <span className={`bri-BodyFont`}>
                        <ProductPrice
                            className="bri-BodyFont"
                            product={product}
                        />
                    </span>
                </div>
            ) : (
                <div>&nbsp;</div>
            ),
        []
    );

    const getStickyHeader = useCallback(() => {
        if (
            !isProductComparisonFeatureItem(mainFeatureItem) ||
            !mainFeatureItem.table
        ) {
            return null;
        }
        return (
            <div className={'bri-ProductsComparison-sticky-header-wrapper'}>
                <div
                    className={`bri-ProductsComparison-sticky-header ${
                        isStickyHeaderVisible ? 'bri-visible' : ''
                    }`}
                >
                    {[...mainFeatureItem.table.products, null]
                        .slice(0, 3)
                        .map(getStickyHeaderItem)}
                </div>
            </div>
        );
    }, [mainFeatureItem, isStickyHeaderVisible, getStickyHeaderItem]);

    const onScroll = useCallback(() => {
        if (!scrollableRef.current || !mainDataRef.current) {
            return;
        }
        const isInView = checkInView(
            scrollableRef.current,
            mainDataRef.current
        );
        if (!isInView && !isStickyHeaderVisible) {
            setStickyHeaderVisible(true);
        } else if (isInView && isStickyHeaderVisible) {
            setStickyHeaderVisible(false);
        }
    }, [scrollableRef.current, mainDataRef.current, isStickyHeaderVisible]);

    if (!isProductComparisonFeatureItem(mainFeatureItem)) {
        return null;
    }
    const {
        loadingStatus: tableLoadingStatus,
        table,
        comparingProductIds
    } = mainFeatureItem;
    return (
        <div className={`bri-ProductsComparison`}>
            <div
                className={`bri-ProductsComparison-body bri-SlimScroll`}
                ref={scrollableRef}
                onScroll={onScroll}
            >
                {tableLoadingStatus === 'success' && table && (
                    <>
                        {getStickyHeader()}
                        <ProductsComparisonTable
                            groups={table.groups}
                            products={table.products}
                            suggestedProductsQuantity={
                                table.products.filter(
                                    product =>
                                        !comparingProductIds.includes(
                                            product.productId
                                        )
                                ).length
                            }
                            onAddProductToCart={({ productId }) =>
                                dispatch.featureState.startFeature({
                                    featureType: 'product-detail',
                                    productId,
                                    source: 'brilliant'
                                })
                            }
                            onRemoveProduct={productId => {
                                dispatch.featureState.removeProductFromComparison(
                                    productId
                                );
                                dispatch.productComparisonState.removeProduct(
                                    productId
                                );
                            }}
                            onClose={() => {
                                if (mainFeatureItem.previousMainFeatureItem) {
                                    dispatch.featureState.backToPreviousFeatureItems();
                                } else {
                                    dispatch.productComparisonState.setSelectedProducts(
                                        table.products
                                    );
                                    dispatch.uiState.setMode('button');
                                }
                            }}
                            mainDataRef={mainDataRef}
                            showExpandButtons={false}
                            expandAllGroups
                        />
                    </>
                )}
                {tableLoadingStatus === 'loading' && (
                    <Loader
                        title={t('PRODUCT_COMPARISON_LOADING_TITLE')}
                        subtitle={t('PRODUCT_COMPARISON_LOADING_SUBTITLE')}
                    />
                )}
                {tableLoadingStatus === 'error' && (
                    <ProductComparisonError
                        onRetryClick={() =>
                            dispatch.featureState.startFeature({
                                featureType: 'product-comparison'
                            })
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default ProductsComparison;

function checkInView(container: HTMLDivElement, element: HTMLAnchorElement) {
    //Get container properties
    const cTop = container.scrollTop;
    // const cBottom = cTop + container.clientHeight;

    //Get element properties
    const eTop = element.offsetTop;
    // const eBottom = eTop + element.clientHeight;

    //Check if in view
    // const isTotal = eTop >= cTop && eBottom <= cBottom;
    const isTotal = eTop >= cTop;

    //Return outcome
    return isTotal;
}
